import PropTypes from "prop-types"
import React, { useState, useRef, useEffect, useCallback } from "react"
import Button from "components/ui/Button"
import { trackEvent } from "lib/googleTagManager"
import { isFreemailDomain } from "lib/freemailDomains"
import submitHubspotForm from "lib/submitHubspotForm"
import * as css from "./ContactForm.module.css"

// Resolved once per page load — timezone doesn't change during a session
const VISITOR_TZ =
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "America/Los_Angeles"

const EMPTY_FORM = {
  firstname: "",
  lastname: "",
  email: "",
  company: "",
  jobtitle: "",
  message: "",
}

function formatSlot(millis) {
  const d = new Date(millis)
  const day = d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: VISITOR_TZ,
  })
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: VISITOR_TZ,
  })
  return { day, time }
}

function SlotGrid({ slots, selectedSlot, onSelect }) {
  return (
    <div className={css.slotGrid}>
      {slots.map((slot) => {
        const { day, time } = formatSlot(slot.startMillisUtc)
        const isSelected = selectedSlot?.startMillisUtc === slot.startMillisUtc
        return (
          <button
            key={slot.startMillisUtc}
            type="button"
            className={`${css.slotChip} ${isSelected ? css.slotChipSelected : ""}`}
            onClick={() => onSelect(isSelected ? null : slot)}
          >
            <span className={css.slotDay}>{day}</span>
            <span className={css.slotTime}>{time}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Inline contact sales form with API submission, freemail detection,
 * and HubSpot-targeted meeting slot picker for qualified visitors.
 *
 * The default behavior asks for company + job title only from freemail
 * users (we infer company from corporate domains). Some HubSpot forms
 * — partner-specific contact pages like /databricks/ — require job
 * title unconditionally, so set `requireJobTitle` to keep the job-title
 * field visible and submitted for everyone. Without this, corporate
 * users get a 400 from HubSpot for missing-required-field.
 */
function ContactForm({
  formId,
  trackSubmit,
  onSubmit,
  requireJobTitle = false,
}) {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)
  const [bookingError, setBookingError] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [slotsState, setSlotsState] = useState({ status: "idle", slots: [] })
  const formRef = useRef(null)
  const hasTrackedView = useRef(false)

  const requireCompany = isFreemailDomain(formData.email)
  const emailDomain = formData.email.split("@")[1] ?? ""
  const domainParts = emailDomain.split(".")
  const tld = domainParts[domainParts.length - 1]
  const hasCompleteDomain =
    domainParts.length >= 2 && tld.length >= 2 && !requireCompany

  // Cookie-based lookup — fires once on mount
  const [cookieLookup, setCookieLookup] = useState({
    status: "idle",
    found: false,
    inPipeline: false,
    rep: null,
  })

  useEffect(() => {
    const utk =
      typeof document !== "undefined"
        ? document.cookie
            .split("; ")
            .find((c) => c.startsWith("hubspotutk="))
            ?.split("=")[1]
        : null

    if (!utk) {
      setCookieLookup({
        status: "done",
        found: false,
        inPipeline: false,
        rep: null,
      })
      return
    }

    setCookieLookup({
      status: "loading",
      found: false,
      inPipeline: false,
      rep: null,
    })
    fetch("/.netlify/functions/lookup-contact-utk", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ utk }),
    })
      .then((r) => r.json())
      .then((data) => {
        setCookieLookup({
          status: "done",
          found: !!data.found,
          inPipeline: !!data.inPipeline,
          rep: data.rep ?? null,
        })
      })
      .catch(() => {
        setCookieLookup({
          status: "done",
          found: false,
          inPipeline: false,
          rep: null,
        })
      })
  }, [])

  // Domain-based lookup
  const [domainLookup, setDomainLookup] = useState({
    status: "idle",
    inPipeline: false,
    rep: null,
  })

  useEffect(() => {
    if (!hasCompleteDomain) {
      setDomainLookup({ status: "idle", inPipeline: false, rep: null })
      return
    }

    let cancelled = false

    // Debounce: wait 300ms after the last keystroke before firing
    const timer = setTimeout(() => {
      setDomainLookup({ status: "loading", inPipeline: false, rep: null })

      fetch("/.netlify/functions/lookup-company-domain", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: emailDomain }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (!cancelled) {
            setDomainLookup({
              status: "done",
              inPipeline: !!data.inPipeline,
              rep: data.rep ?? null,
            })
          }
        })
        .catch(() => {
          if (!cancelled) {
            setDomainLookup({
              status: "done",
              inPipeline: false,
              rep: {
                name: "Zane",
                meetingSlug: "zane-aitken/introductory-call",
              },
            })
          }
        })
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [emailDomain, hasCompleteDomain])

  // Qualification — either source qualifies; either source can block
  const cookieBlocked =
    cookieLookup.status === "done" && cookieLookup.inPipeline
  const domainBlocked =
    hasCompleteDomain &&
    domainLookup.status === "done" &&
    domainLookup.inPipeline
  const cookieQualified =
    cookieLookup.status === "done" &&
    cookieLookup.found &&
    !cookieLookup.inPipeline
  const domainQualified =
    hasCompleteDomain &&
    domainLookup.status === "done" &&
    !domainLookup.inPipeline
  const isQualified =
    !cookieBlocked && !domainBlocked && (cookieQualified || domainQualified)

  const effectiveRep = domainLookup.rep ?? cookieLookup.rep
  const effectiveRepSlug = isQualified
    ? (effectiveRep?.meetingSlug ?? null)
    : null

  // Clear slot when no longer qualified
  useEffect(() => {
    if (!isQualified) {
      setSelectedSlot(null)
      setSlotsState({ status: "idle", slots: [] })
    }
  }, [isQualified])

  // Fetch slots when rep is known
  useEffect(() => {
    if (!effectiveRepSlug) return

    let cancelled = false
    setSlotsState({ status: "loading", slots: [] })

    const timezone = VISITOR_TZ

    fetch("/.netlify/functions/get-meeting-slots", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug: effectiveRepSlug, timezone }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled)
          setSlotsState({ status: "done", slots: data.slots ?? [] })
      })
      .catch(() => {
        if (!cancelled) setSlotsState({ status: "done", slots: [] })
      })

    return () => {
      cancelled = true
    }
  }, [effectiveRepSlug])

  // Track when the form scrolls into view
  useEffect(() => {
    const el = formRef.current
    if (!el || hasTrackedView.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true
          trackEvent("contactSalesFormViewed", {
            formId,
            pageSlug:
              typeof window !== "undefined" ? window.location.pathname : "",
            pageName: typeof document !== "undefined" ? document.title : "",
          })
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [formId])

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setBookingError(null)

    try {
      const data = {
        ...formData,
        source_page:
          typeof window !== "undefined" ? window.location.pathname : "",
      }
      if (!requireCompany) {
        delete data.company
        if (!requireJobTitle) {
          delete data.jobtitle
        }
      }

      // Form submission is the required step
      const formResponse = await submitHubspotForm(formId, data)

      if (!formResponse.ok) {
        setError("We couldn't send your message — please try again.")
        return
      }

      // Form succeeded — independently await booking
      let bookingOk = !selectedSlot || !effectiveRepSlug
      if (selectedSlot && effectiveRepSlug) {
        const timezone =
          typeof Intl !== "undefined"
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : "UTC"
        try {
          const bookRes = await fetch("/.netlify/functions/book-meeting", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              slug: effectiveRepSlug,
              startTime: selectedSlot.startMillisUtc,
              duration: 1800000,
              firstName: formData.firstname,
              lastName: formData.lastname,
              email: formData.email,
              timezone,
            }),
          })
          const bookData = await bookRes.json().catch(() => ({}))
          bookingOk = !!bookData.booked
        } catch {
          bookingOk = false
        }
      }

      if (trackSubmit) {
        trackEvent("formSubmission", {
          formSubmissionEventName: trackSubmit,
          meetingBooked: !!selectedSlot && bookingOk,
        })
      }

      if (bookingOk) {
        // Everything worked — let the parent handle the success screen
        onSubmit?.()
      } else {
        // Form sent but meeting not booked — show partial success inline
        setBookingError(true)
        setSubmitted(true)
      }
    } catch (_err) {
      setError("We couldn't send your message — please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className={css.partialSuccess} ref={formRef}>
        <svg
          className={css.checkIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <div>
          <p className={css.partialSuccessMessage}>Your message was sent!</p>
          {bookingError && (
            <p className={css.bookingError}>
              We weren&apos;t able to book the time slot automatically.{" "}
              {effectiveRepSlug ? (
                <a
                  href={`https://meetings.hubspot.com/${effectiveRepSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.bookingErrorLink}
                >
                  Book directly here
                </a>
              ) : (
                "Please reply to your confirmation email to schedule a time."
              )}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className={css.form}>
      <div className={css.nameRow}>
        <div className={css.field}>
          <label htmlFor="cs-firstname" className={css.fieldLabel}>
            First Name <span className={css.required}>*</span>
          </label>
          <input
            type="text"
            id="cs-firstname"
            name="firstname"
            value={formData.firstname}
            onChange={handleChange}
            required
            className={css.input}
          />
        </div>

        <div className={css.field}>
          <label htmlFor="cs-lastname" className={css.fieldLabel}>
            Last Name <span className={css.required}>*</span>
          </label>
          <input
            type="text"
            id="cs-lastname"
            name="lastname"
            value={formData.lastname}
            onChange={handleChange}
            required
            className={css.input}
          />
        </div>
      </div>

      <div className={css.field}>
        <label htmlFor="cs-email" className={css.fieldLabel}>
          Work Email <span className={css.required}>*</span>
          {domainLookup.status === "loading" && (
            <span className={css.lookupSpinner} aria-hidden="true" />
          )}
        </label>
        <input
          type="email"
          id="cs-email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className={css.input}
        />
      </div>

      {requireCompany && (
        <div className={css.freemailFields}>
          <p className={css.freemailNote}>
            Please tell us a bit about your company so we can better assist you.
          </p>
          <div className={css.field}>
            <label htmlFor="cs-company" className={css.fieldLabel}>
              Company Name <span className={css.required}>*</span>
            </label>
            <input
              type="text"
              id="cs-company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required={requireCompany}
              className={css.input}
            />
          </div>
        </div>
      )}

      {(requireCompany || requireJobTitle) && (
        <div className={css.field}>
          <label htmlFor="cs-jobtitle" className={css.fieldLabel}>
            Job Title <span className={css.required}>*</span>
          </label>
          <input
            type="text"
            id="cs-jobtitle"
            name="jobtitle"
            value={formData.jobtitle}
            onChange={handleChange}
            required
            className={css.input}
          />
        </div>
      )}

      <div className={css.field}>
        <label htmlFor="cs-message" className={css.fieldLabel}>
          Message
        </label>
        <textarea
          id="cs-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          className={css.textarea}
          placeholder="Tell us about your project or questions..."
        />
      </div>

      {isQualified && (
        <div className={css.bookingSection}>
          <div className={css.bookingHeader}>
            <span className={css.bookingLabel}>
              Book a call{" "}
              <span className={css.bookingOptional}>(optional)</span>
            </span>
            <span className={css.bookingHint}>
              30-min intro call
              {effectiveRep ? ` with ${effectiveRep.name}` : ""}
            </span>
          </div>

          {slotsState.status === "loading" ? (
            <div className={css.slotGridSkeleton}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className={css.slotChipSkeleton} />
              ))}
            </div>
          ) : slotsState.slots.length === 0 ? (
            <p className={css.slotEmpty}>
              No available times right now — check back soon.
            </p>
          ) : (
            <SlotGrid
              slots={slotsState.slots}
              selectedSlot={selectedSlot}
              onSelect={setSelectedSlot}
            />
          )}
        </div>
      )}

      {error && <p className={css.error}>{error}</p>}

      <Button
        type="submit"
        color="lush"
        size="medium"
        disabled={submitting}
        className={css.submitButton}
      >
        {submitting
          ? "Sending..."
          : selectedSlot
            ? `Book ${formatSlot(selectedSlot.startMillisUtc).time} & Send Message`
            : "Submit"}
      </Button>
    </form>
  )
}

SlotGrid.propTypes = {
  slots: PropTypes.arrayOf(
    PropTypes.shape({ startMillisUtc: PropTypes.number })
  ).isRequired,
  selectedSlot: PropTypes.shape({ startMillisUtc: PropTypes.number }),
  onSelect: PropTypes.func.isRequired,
}

ContactForm.propTypes = {
  formId: PropTypes.string.isRequired,
  trackSubmit: PropTypes.string,
  onSubmit: PropTypes.func,
  requireJobTitle: PropTypes.bool,
}

export default ContactForm
